import { ChangeEvent, DragEvent, HTMLProps, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { UploadWrapper, UploadWrapperProps } from './components/uploadWrapper';

function isAcceptedFileType(file: File, acceptedTypes: string) {
  if (!file.type) return false; // Avoid issues where file.type is empty

  // If '*' is used, accept all files
  if (acceptedTypes.trim() === '*') return true;

  // Split the accepted types into an array
  const accepted = acceptedTypes.split(',');

  return accepted.some((type) => {
    // Check for wildcards (image/*, video/*)
    if (type.includes('/*')) {
      const typePrefix = type.split('/')[0];
      return file.type.startsWith(`${typePrefix}/`);
    }
    // Check for exact MIME types (image/jpeg, video/mp4)
    if (file.type.trim() === type.trim()) {
      return true;
    }
    // Check for file extension matches (e.g., .pdf, .csv)
    if (type.startsWith('.')) {
      const extension = type.trim().toLowerCase();
      return file.name.toLowerCase().endsWith(extension);
    }
    return false;
  });
}

type UploadError = {
  type: 'maxFilesExceeded' | 'invalidFileType';
  files?: File[];
} | null;

export interface UploadProps extends Omit<UploadWrapperProps, 'children'> {
  /**
   * Properties that will be passed to the input element.
   */
  inputProps?: Omit<HTMLProps<HTMLInputElement>, 'hidden' | 'type' | 'multiple'>;

  /**
   * Sets maximum files that are available.
   * If maxFiles is bigger than 1 the input is type multiple.
   * @default 1
   */
  maxFiles?: number;

  /**
   * Handler that is called when user provide files.
   */
  onFileChange?: (files: Array<File>) => void;

  /**
   * Children as a render function that gets error and dragging state.
   */
  children?: ReactNode | ((params: { error: UploadError; dragging: boolean }) => ReactNode);

  /**
   * Current number of uploaded files.
   */
  currentFilesCount?: number;
}

export function Upload({
  children,
  currentFilesCount = 0,
  onFileChange,
  inputProps = {},
  maxFiles = 1,
  state,
  ...restProps
}: UploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState<boolean>(false);
  const [error, setError] = useState<UploadError>(null);

  const accept = inputProps?.accept || '*';

  const currentState = useMemo(() => {
    return dragging ? 'dragdropping' : error ? 'error' : state ? state : 'default';
  }, [dragging, error, state]);

  const preventDragOver = useCallback((event: DragEvent) => {
    event.stopPropagation();
    event.preventDefault();
  }, []);

  const getFilteredFiles = useCallback(
    (inputFiles: File[]) => inputFiles.filter((file) => isAcceptedFileType(file, accept)),
    [accept, maxFiles],
  );

  const handleDragEnter = useCallback(() => {
    if (!onFileChange) return;

    error && setError(null); // reset error state if needed
    setDragging(true);
  }, [setDragging]);

  const handleDragLeave = useCallback(() => {
    setDragging(false);
  }, [setDragging]);

  const handleClick = useCallback(() => {
    error && setError(null); // reset error state if needed
    inputRef.current?.click();
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      error && setError(null); // reset error state if needed

      const files = [...event.dataTransfer.files];

      if (currentFilesCount + files.length > maxFiles) {
        setDragging(false);
        setError({
          type: 'maxFilesExceeded',
        });
        return;
      }

      const permittedFiles = files.filter((file) => !isAcceptedFileType(file, accept));

      if (permittedFiles.length > 0) {
        setDragging(false);
        setError({
          type: 'invalidFileType',
          files: permittedFiles,
        });
        return;
      }

      const filteredFiles = getFilteredFiles([...event.dataTransfer.files]);
      setDragging(false);
      if (filteredFiles.length > 0) {
        onFileChange?.(filteredFiles);
      }
    },
    [setDragging, error, setError, accept, onFileChange, currentFilesCount],
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      event.stopPropagation();

      error && setError(null); // reset error state if needed

      const files = [...event.target.files];

      if (currentFilesCount + files.length > maxFiles) {
        setDragging(false);
        setError({
          type: 'maxFilesExceeded',
        });
        return;
      }

      const permittedFiles = files.filter((file) => !isAcceptedFileType(file, accept));

      if (permittedFiles.length > 0) {
        setError({
          type: 'invalidFileType',
          files: permittedFiles,
        });
        return;
      }

      const filteredFiles = getFilteredFiles(files);

      if (filteredFiles.length > 0) {
        onFileChange?.(filteredFiles);

        if (inputRef.current) {
          inputRef.current.value = '';
        }
      }
    },
    [setDragging, error, setError, accept, onFileChange, currentFilesCount],
  );

  return (
    <UploadWrapper
      role="button"
      tabIndex={0}
      aria-label="Upload file"
      state={currentState}
      onClick={handleClick}
      onDragOver={preventDragOver}
      onDrop={handleDrop}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onKeyDown={(event) => {
        if (event.key === 'Enter') handleClick();
      }}
      {...restProps}
    >
      {typeof children === 'function' ? children({ error, dragging }) : children}
      <input ref={inputRef} onChange={handleInputChange} {...inputProps} hidden type="file" multiple={maxFiles > 1} />
    </UploadWrapper>
  );
}
